import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Button from 'shared/components/UC/Button';
import Alert from 'shared/components/UC/Alert';
import SubmissionConfirmationModal from './SubmissionConfirmationModal';
import { EvaluatorApplicationDetails, Evaluation } from '../../types/evaluator';
import styles from './SummaryStep.module.css';

interface SummaryStepProps {
  application: EvaluatorApplicationDetails;
  evaluation: Evaluation | null;
  onSubmit: () => Promise<void>;
}

const evaluationCriteria = [
  {
    id: 'originality_risk',
    name: 'Originalidad y riesgo de la investigación',
    weight: 20,
  },
  {
    id: 'national_relevance',
    name: 'Pertinencia nacional e impacto potencial',
    weight: 20,
  },
  {
    id: 'global_scalability',
    name: 'Escalabilidad global de la idea del proyecto',
    weight: 15,
  },
  {
    id: 'international_collaboration',
    name: 'Nivel de colaboración internacional y levantamiento de fondos adicionales',
    weight: 20,
  },
  {
    id: 'technical_viability',
    name: 'Viabilidad técnica de la propuesta, planificación',
    weight: 10,
  },
  { id: 'results_quality', name: 'Calidad de los resultados', weight: 15 },
];

const SummaryStep: React.FC<SummaryStepProps> = ({
  application,
  evaluation,
  onSubmit,
}) => {
  const { watch } = useFormContext();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formData = watch();
  const criteriaEvaluations = formData.criteriaEvaluations || {};

  // Calculate weighted average score
  const calculateWeightedScore = () => {
    let totalWeightedScore = 0;
    let totalWeight = 0;

    evaluationCriteria.forEach((criterion) => {
      const evaluation = criteriaEvaluations[criterion.id];
      if (
        evaluation &&
        evaluation.score !== null &&
        evaluation.score !== undefined &&
        evaluation.score !== ''
      ) {
        const numericScore = Number(evaluation.score);
        if (!isNaN(numericScore)) {
          totalWeightedScore += numericScore * criterion.weight;
          totalWeight += criterion.weight;
        }
      }
    });

    return totalWeight > 0
      ? (totalWeightedScore / totalWeight).toFixed(2)
      : '0.00';
  };

  const isEvaluationComplete = () => {
    const hasAllScores = evaluationCriteria.every((criterion) => {
      const evaluation = criteriaEvaluations[criterion.id];
      return (
        evaluation &&
        evaluation.score !== null &&
        evaluation.score !== undefined &&
        evaluation.score !== '' &&
        !isNaN(Number(evaluation.score))
      );
    });

    const hasAllComments = evaluationCriteria.every((criterion) => {
      const evaluation = criteriaEvaluations[criterion.id];
      return (
        evaluation && evaluation.comments && evaluation.comments.trim() !== ''
      );
    });

    // Budget comments are optional (P3): they do not gate submission.

    return (
      hasAllScores &&
      hasAllComments &&
      !!formData.overallComments?.trim()
    );
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await onSubmit();
      setShowConfirmationModal(false);
    } catch (error) {
      console.error('Error submitting evaluation:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const weightedScore = calculateWeightedScore();
  const isComplete = isEvaluationComplete();
  const isSubmitted = evaluation?.submittedAt;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Resumen de Evaluación</h2>
        {isSubmitted ? (
          // Sello de estado enviado, prominente y al golpe de vista (verde).
          // Consolida el indicador de "Enviada" en el encabezado del Resumen.
          <p className={styles.submittedStamp}>
            <i className="uc-icon" aria-hidden="true">
              check_circle
            </i>
            Enviada el {formatDate(evaluation?.submittedAt ?? null)}
          </p>
        ) : (
          <p className={styles.subtitle}>
            Revise su evaluación antes del envío final. Una vez enviada, no
            podrá ser modificada.
          </p>
        )}
      </div>

      <div className={styles.content}>
        {/* Application Summary */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Información de la Postulación</h3>
          <div className={styles.applicationSummary}>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <label>Número de Postulación:</label>
                <span>#{application.applicationNumber}</span>
              </div>
              <div className={styles.summaryItem}>
                <label>Título del Proyecto:</label>
                <span>
                  {application?.answers?.sectionAnswers?.PROJECT_INFORMATION
                    ?.projectTitle ?? 'Sin título'}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <label>Postulante:</label>
                <span>{application.user.name}</span>
              </div>
              <div className={styles.summaryItem}>
                <label>Fecha de Envío:</label>
                <span>{formatDate(application.submittedAt)}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Evaluation Summary */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Resumen de Evaluación</h3>

          <div className={styles.evaluationSummary}>
            <div className={styles.scoreOverview}>
              <div className={styles.weightedScoreCard}>
                <h4>Puntuación Ponderada Total</h4>
                <div className={styles.scoreDisplay}>
                  <span className={styles.score}>{weightedScore}</span>
                  <span className={styles.maxScore}>/ 5.0</span>
                </div>
              </div>
            </div>

            <div className={styles.criteriaBreakdown}>
              <h4>Desglose por Criterios</h4>
              <div className={styles.criteriaList}>
                {evaluationCriteria.map((criterion) => {
                  const criterionEval = criteriaEvaluations[criterion.id] || {};
                  const score = criterionEval.score;
                  const comments = criterionEval.comments || '';

                  return (
                    <div key={criterion.id} className={styles.criterionSummary}>
                      <div className={styles.criterionHeader}>
                        <h5 className={styles.criterionName}>
                          {criterion.name}
                        </h5>
                        <div className={styles.criterionMeta}>
                          <span className={styles.criterionWeight}>
                            {criterion.weight}%
                          </span>
                          <span
                            className={`${styles.criterionScore} ${score === null || score === undefined || score === '' || isNaN(Number(score)) ? styles.missingScore : ''}`}
                          >
                            {score !== null &&
                            score !== undefined &&
                            score !== '' &&
                            !isNaN(Number(score))
                              ? Number(score).toFixed(1)
                              : '-'}
                          </span>
                        </div>
                      </div>
                      <div className={styles.criterionComments}>
                        <p>{comments || 'Sin comentarios'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Budget Comments (optional) */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Comentarios al Presupuesto</h3>
          <div className={styles.budgetCommentsSummary}>
            <div className={styles.budgetCommentsCard}>
              <div className={styles.budgetCommentsContent}>
                {formData.budgetComments &&
                formData.budgetComments.trim() !== '' ? (
                  <p>{formData.budgetComments}</p>
                ) : (
                  <p>Sin comentarios al presupuesto.</p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Percepción global (la Recomendación General fue eliminada — P5 2026-07-14) */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Percepción global de la propuesta
          </h3>
          <div className={styles.recommendationSummary}>
            <div className={styles.recommendationCard}>
              {formData.overallComments ? (
                <div className={styles.overallComments}>
                  <p>{formData.overallComments}</p>
                </div>
              ) : (
                <p className={styles.missingNotice}>Aún sin redactar.</p>
              )}
            </div>
          </div>
        </section>

        {/* Conflict Declaration */}
        {formData.conflictsDeclared && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Declaración de Conflictos</h3>
            <div className={styles.conflictSummary}>
              <div className={styles.conflictCard}>
                <div className={styles.conflictHeader}>
                  <span className={styles.conflictIcon}></span>
                  <span>Se han declarado conflictos de interés</span>
                </div>
                {formData.conflictDetails && (
                  <div className={styles.conflictDetails}>
                    <h5>Detalles del Conflicto</h5>
                    <p>{formData.conflictDetails}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Submission Status */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Estado de Envío</h3>
          <div className={styles.submissionStatus}>
            {isSubmitted ? (
              <Alert variant="success">
                Evaluación enviada exitosamente el{' '}
                {formatDate(evaluation.submittedAt)}. Ya no puede ser
                modificada. Gracias por su participación en el proceso de
                evaluación.
              </Alert>
            ) : (
              <div className={styles.pendingCard}>
                <div className={styles.readinessCheck}>
                  <h4>Estado de la Evaluación</h4>
                  <div className={styles.checkList}>
                    <div
                      className={`${styles.checkItem} ${isComplete ? styles.checkComplete : styles.checkIncomplete}`}
                    >
                      <span className={styles.checkIcon}>
                        {isComplete ? '' : ''}
                      </span>
                      <span>
                        {isComplete
                          ? 'Evaluación completa y lista para envío'
                          : 'Evaluación incompleta - complete todos los criterios con puntuaciones válidas'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.submissionActions}>
                  <Button
                    variant="cta"
                    size="default"
                    onClick={() => setShowConfirmationModal(true)}
                    className={styles.submitButton}
                    disabled={!isComplete || submitting}
                  >
                    Enviar Evaluación Final
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <SubmissionConfirmationModal
          application={application}
          evaluationSummary={{
            weightedScore: parseFloat(weightedScore),
            criteriaCount: evaluationCriteria.length,
            hasConflicts: formData.conflictsDeclared,
          }}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirmationModal(false)}
          submitting={submitting}
        />
      )}
    </div>
  );
};

export default SummaryStep;
